import {LessonGenerationJob} from '@models/lesson-generation-types';

/** Largest PDF a lesson can be built from. */
export const MAX_LESSON_SOURCE_BYTES = 30 * 1024 * 1024;

/** Share of the progress bar for reading the PDF; building the lesson is the rest. */
export const READING_SHARE = 90;

// Time constant for the estimated reading progress (~63% of the stage after this long)
const EXPECTED_READING_MS = 4 * 60 * 1000;

/** Why a file can't be used to build a lesson, if it can't. */
export function lessonSourceError(
  file: File,
): 'unsupported' | 'too-large' | undefined {
  const isPdf =
    file.name.toLowerCase().endsWith('.pdf') &&
    (!file.type || file.type === 'application/pdf');
  if (!isPdf) {
    return 'unsupported';
  }
  if (file.size > MAX_LESSON_SOURCE_BYTES) {
    return 'too-large';
  }
  return undefined;
}

/** The lesson is named after the file, without its extension. */
export function lessonTitle(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, '').trim() || fileName;
}

/** What the agent is asked to do; the chat shows `display` instead. */
export function buildLessonMessage(fileName: string): {
  message: string;
  display: string;
} {
  return {
    message: [
      `Build a complete lesson from the attached document "${fileName}".`,
      'Work through the whole document in order, a few sections at a time: create_cards_from_document for its text',
      "(always, never create_text_card: it copies the sections exactly, with the document's figures),",
      'create_table_card for its tables (copy the cells exactly), create_multiple_choice_card or',
      'create_free_response_card for its questions (word for word, one per card), and create_matching_card',
      'for any matching or label-a-diagram activities.',
      'Every card you make is added to the lesson automatically, in the order you make them, so keep the',
      "document's order and don't make the same content twice.",
      "Don't ask me anything first and don't stop part way: make all the cards now.",
      "Don't set up data collection or visualizations.",
      'When you are done, say in one sentence what the lesson covers.',
    ].join(' '),
    display: `Build a lesson from "${fileName}".`,
  };
}

/**
 * Progress bar percentage while the PDF is read. Short PDFs are read in one pass with no
 * progress reported, so it also creeps forward with time: it approaches, but never reaches,
 * the end of the reading stage until the PDF is read (reported = 1).
 */
export function readingProgress(elapsedMs: number, reported: number): number {
  const estimated = 1 - Math.exp(-elapsedMs / EXPECTED_READING_MS);
  return Math.round(Math.max(estimated, reported) * READING_SHARE);
}

/** True while a lesson is still being built (e.g. to warn before the page is closed). */
export function isBuilding(jobs: LessonGenerationJob[]): boolean {
  return jobs.some((j) => j.status === 'queued' || j.status === 'processing');
}
